/** @jsxImportSource @emotion/react */
import { StyledRow } from 'components/atoms/DataTable'
import Input from 'components/atoms/Input'
import Pagination from 'components/atoms/Pagination'
import Select from 'components/atoms/Select'
import { StatePill } from 'components/lab/atoms/StatePill'
import { ApplyPaymentModal } from 'components/lab/billingEraReports/ApplyPaymentModal'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from 'components/lab/user/permissions'
import { filterPayerOption, formatPayerOptionLabel, viaName } from 'components/lab/payer/PayerOptionLabel'
import { BillingLoading } from 'components/lab/billingEraReports/BillingLoading'
import { ERA_TEST_TYPE_OPTIONS } from 'components/lab/billingEraReports/constants'
import { ClaimIdentifierLink, ClaimPatientName } from 'components/lab/billingEraReports/EraClaimChargeLinks'
import {
    AUTO_POST_STATE_OPTIONS,
    getAutoPostReasons,
    getAutoPostStateColor,
    getAutoPostStateLabel,
    getClaimMatchStatus,
    getClaimStateColor,
    getClaimStateLabel,
    getPostingActionLabel,
    getMatchMethodLabel,
    getMatchStatusColor,
    getMatchStatusLabel,
    MATCH_STATUS_OPTIONS,
    REVIEW_STATE_OPTIONS,
} from 'components/lab/billingEraReports/claimState'
import { confirm } from 'components/molecules/Confirmation'
import React from 'react'
import { Button } from 'react-bootstrap'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'
import {
    useAcceptEraClaimMatchMutation,
    useArchiveBillingEraClaimMutation,
    useGetEraExceptionQueueQuery,
    useResolveEraCrossoverPayerMutation,
} from 'store/services/billingCharges'
import { useGetEraPostingPlanQuery } from 'store/services/billingPayments'
import { useGetLabPayersQuery } from 'store/services/payer'
import {
    EraAutoPostState,
    EraClaimMatchStatus,
    EraClaimReviewState,
    EraExceptionQueueItem,
    EraExceptionQueueQuery,
    TestCode,
} from 'store/types'
import { useDebounce } from 'use-debounce'
import { formatCalendarDate, formatEasternDate } from 'helpers/dates'

const Money = ({ v }: { v?: string | number | null }) => (
  <span>{v ? `$${Number(v).toFixed(2)}` : '$0.00'}</span>
)


/**
 * Why the claim is in the queue, in one line. Reads the reason the classifier
 * recorded, so the explanation and the state the row was filtered on agree.
 */
const StateReason = ({ claim }: { claim: EraExceptionQueueItem }) => {
  const reason = claim.stateReason

  if (!reason) {
    return null
  }

  const parts: string[] = []

  if (reason.actionableCodes?.length) {
    parts.push(`CARC ${reason.actionableCodes.join(', ')}`)
  }

  if (reason.deniedLines?.length) {
    parts.push(`line ${reason.deniedLines.join(', ')} paid $0`)
  }

  if (!parts.length) {
    return null
  }

  return <div css={{ fontSize: 11, color: '#6c757d' }}>{parts.join(' · ')}</div>
}

/**
 * Why a claim is or is not going to post itself.
 *
 * Shown only once the claim has been evaluated: before auto-posting is
 * switched on, and for anything imported before it existed, there is no
 * verdict to report and an empty cell says that more honestly than a badge
 * reading "unknown".
 */
const AutoPostCell = ({ claim }: { claim: EraExceptionQueueItem }) => {
  if (!claim.autoPostState) {
    return <span css={{ fontSize: 11, color: '#adb5bd' }}>—</span>
  }

  const reasons = getAutoPostReasons(claim.autoPostReason)

  return (
    <>
      <span
        css={{ color: getAutoPostStateColor(claim.autoPostState), fontWeight: 600, fontSize: 12 }}>
        {getAutoPostStateLabel(claim.autoPostState)}
      </span>
      {reasons.length > 0 && (
        <div css={{ fontSize: 11, color: '#6c757d' }}>{reasons.join(' · ')}</div>
      )}
      {claim.autoPostRetryAfter && (
        <div css={{ fontSize: 11, color: '#6c757d' }}>
          Retries {formatEasternDate(claim.autoPostRetryAfter)}
        </div>
      )}
    </>
  )
}

/**
 * The charge column. A confirmed link is stated flatly; a suggestion is offered
 * with the evidence behind it and an accept button, because accepting is the
 * only way a suggestion ever becomes the charge a payment posts against.
 */
const ChargeCell = ({ claim }: { claim: EraExceptionQueueItem }) => {
  const canPostInsurancePayments = useLabPermission(UserPermission.POST_INSURANCE_PAYMENTS)
  const [acceptMatch, { isLoading }] = useAcceptEraClaimMatchMutation()
  const chargeId = claim.chargeId
  const matchStatus = getClaimMatchStatus(claim)

  if (!chargeId) {
    return (
      <div>
        <StatePill
          color={getMatchStatusColor(matchStatus)}
          label={getMatchStatusLabel(matchStatus)}
          tooltip={getMatchMethodLabel(claim)}
        />
        <div css={{ fontSize: 11, color: '#6c757d' }}>
          {matchStatus === 'AMBIGUOUS'
            ? 'Several charges fit — pick one on the payment form'
            : 'No charge found for this claim'}
        </div>
      </div>
    )
  }

  return (
    <div>
      <StatePill
        color={getMatchStatusColor(matchStatus)}
        label={getMatchStatusLabel(matchStatus)}
        tooltip={getMatchMethodLabel(claim)}
      />
      <div css={{ fontSize: 12, marginTop: 2 }}>
        {claim.chargePatientName || '-'}
        {claim.chargeOrderId ? ` · Order ${claim.chargeOrderId}` : ''}
      </div>
      <div css={{ fontSize: 11, color: '#6c757d' }}>
        {formatCalendarDate(claim.chargeAppointmentDate)} · <Money v={claim.chargeTotalPlanAmount} />
      </div>
      {matchStatus === 'SUGGESTED' && canPostInsurancePayments && (
        <Button
          size="sm"
          variant="outline-success"
          className="mt-1"
          disabled={isLoading}
          onClick={async () => {
            try {
              await acceptMatch({ eraId: claim.eraId as string, claimId: claim.id }).unwrap()
              toast.success('Charge linked to claim')
            } catch (e: any) {
              toast.error(e?.data?.message || 'Failed to link the charge')
            }
          }}>
          Accept match
        </Button>
      )}
    </div>
  )
}

type PayerOption = { value: number; label: string; matchedName?: string | null }

// Name, record id and industry payer id: several records share a name (the
// Cigna family), and the ids are what tell a biller which one this is.
const payerLabel = (payer: { id: number; name?: string | null; uPayerId?: string | null }): string =>
  [payer.name || 'Payer', `#${payer.id}`, payer.uPayerId || null].filter(Boolean).join(' · ')

const selectStyles = {
  container: (base: Record<string, unknown>) => ({ ...base, minWidth: 220, fontSize: 12 }),
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
}

/**
 * A claim the payer forwarded to a carrier the charge does not carry. The 835
 * names it (NM1*TT); the server reads that id and name against our payer
 * records, and the row offers what it found — the one payer to add, the few to
 * choose between, or a search when nothing matched. Adding it unblocks
 * posting: with no secondary on file the transfer falls to the patient, who
 * then pays a bill their secondary was about to settle. The choice is
 * remembered against the id, so the next remittance from that carrier posts
 * on its own.
 */
const CrossoverSuggestion = ({ claim }: { claim: EraExceptionQueueItem }) => {
  const canPostInsurancePayments = useLabPermission(UserPermission.POST_INSURANCE_PAYMENTS)
  const [resolveCrossoverPayer, { isLoading }] = useResolveEraCrossoverPayerMutation()
  const [chosen, setChosen] = React.useState<PayerOption | null>(null)
  const [payerSearch, setPayerSearch] = React.useState('')
  // The biller overruling what our records suggested: a wrong alias or a
  // wrong learned mapping proposes the wrong payer with full confidence, and
  // the only fix that sticks is choosing the right one here — that choice is
  // remembered as MANUAL and outranks whatever produced the suggestion.
  const [searchInstead, setSearchInstead] = React.useState(false)
  const [debouncedPayerSearch] = useDebounce(payerSearch, 300)

  const reasons = claim.autoPostReason?.reasons ?? []
  const needsSecondary = reasons.includes('CROSSOVER_NO_SECONDARY')
  const beyondTertiary = reasons.includes('CROSSOVER_BEYOND_TERTIARY')
  const forwardedTo = claim.nextResponsible && claim.nextResponsible !== '-' ? claim.nextResponsible : null
  const candidates = claim.crossoverCandidates ?? []
  const mismatch = claim.crossoverResolution === 'DOWNSTREAM_MISMATCH'
  // Behind the payer that paid: the secondary after a primary, the tertiary after a secondary.
  const rank = claim.crossoverPosition === 3 ? 'tertiary' : claim.crossoverPosition === 2 ? 'secondary' : 'next payer'
  const needsSearch =
    needsSecondary
    && !!claim.chargeId
    && !mismatch
    && (searchInstead || (!claim.crossoverPayer && !candidates.length))

  const { data: foundPayers } = useGetLabPayersQuery(
    { name: debouncedPayerSearch, active: true },
    { skip: !needsSearch || !debouncedPayerSearch },
  )

  if ((!needsSecondary && !beyondTertiary) || (!forwardedTo && !claim.crossoverPayerId)) {
    return null
  }

  // Adding a payer changes who we bill for this charge, and the answer is
  // remembered for every future remittance from this carrier — worth a second
  // look before a misclick teaches the system the wrong payer.
  const add = async (option: PayerOption | null) => {
    if (!option) {
      return
    }

    try {
      await confirm('', {
        title: `Add ${option.label} as ${rank}?`,
        description: () => (
          <div>
            <div>
              <b>{option.label}</b>
              {` will be added to the charge as the ${rank} payer`}
              {claim.chargePatientName ? ` for ${claim.chargePatientName}` : ''}
              {claim.chargeOrderId ? ` (order ${claim.chargeOrderId})` : ''}.
            </div>
            <div className="mt-2">
              {claim.crossoverPayerId
                ? `Payer id ${claim.crossoverPayerId} (${forwardedTo || 'this carrier'}) will be remembered as ${option.label} for future remittances.`
                : `Future remittances forwarded to ${forwardedTo || 'this carrier'} will be matched by name.`}
            </div>
            <div className="mt-2">The claim will post on the next auto-posting run.</div>
          </div>
        ),
        yesAction: 'Add',
        noAction: 'Cancel',
      })
    } catch {
      return
    }

    try {
      await resolveCrossoverPayer({
        eraId: claim.eraId as string,
        claimId: claim.id,
        payerId: option.value,
      }).unwrap()
      toast.success(`${option.label} added as ${rank} — the claim will post on the next run`)
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to add the payer')
    }
  }

  const picker = (options: PayerOption[], placeholder: string, searchable: boolean) => (
    <div className="d-flex align-items-center mt-1" css={{ gap: 6 }}>
      <ReactSelect
        isClearable
        isSearchable={searchable}
        placeholder={placeholder}
        options={options}
        formatOptionLabel={formatPayerOptionLabel}
        filterOption={filterPayerOption}
        value={chosen}
        onChange={(item) => setChosen(item ? { value: Number(item.value), label: item.label } : null)}
        onInputChange={searchable ? (value) => setPayerSearch(value) : undefined}
        inputValue={searchable ? payerSearch : undefined}
        onBlur={searchable ? () => setPayerSearch('') : undefined}
        menuPortalTarget={document.body}
        styles={selectStyles}
      />
      <Button size="sm" variant="outline-info" disabled={!chosen || isLoading} onClick={() => add(chosen)}>
        Add
      </Button>
    </div>
  )

  const switchToSearch = () => {
    setChosen(null)
    setSearchInstead(true)
  }

  const searchBody = (hint: string) => (
    <>
      <div>{hint}</div>
      {picker(
        (foundPayers ?? []).map((payer) => ({
          value: Number(payer.payerId),
          label: payerLabel({ id: Number(payer.payerId), name: payer.name, uPayerId: payer.uPayerId }),
          matchedName: payer.matchedName,
        })),
        'Search payers…',
        true,
      )}
    </>
  )

  const linkStyle = { padding: 0, fontSize: 11, verticalAlign: 'baseline' }

  let body: React.ReactNode

  if (beyondTertiary) {
    body = <div>Forwarded by the tertiary payer — nothing to post, nobody to add.</div>
  } else if (!claim.chargeId) {
    body = <div>Link the claim to a charge before adding the payer.</div>
  } else if (mismatch) {
    body = (
      <div>
        {claim.crossoverPayer
          ? `Our records say this is ${payerLabel(claim.crossoverPayer)}, but the charge carries a different next payer. `
          : "The charge's next payer is not the carrier named. "}
        Edit the charge's payers.
      </div>
    )
  } else if (!canPostInsurancePayments) {
    body = <div>No matching payer on the charge.</div>
  } else if (searchInstead) {
    body = (
      <>
        {searchBody(`Search for the ${rank} to add. The choice will be remembered for this carrier.`)}
        <Button variant="link" css={linkStyle} onClick={() => { setChosen(null); setSearchInstead(false) }}>
          Back to the suggestion
        </Button>
      </>
    )
  } else if (claim.crossoverPayer) {
    const payer = claim.crossoverPayer

    body = (
      <>
        <Button
          size="sm"
          variant="outline-info"
          className="mt-1"
          disabled={isLoading}
          title={`Add ${payerLabel(payer)} to the charge as ${rank}`}
          onClick={() => add({ value: payer.id, label: payerLabel(payer) })}>
          {`Add ${payerLabel(payer)} as ${rank}`}
        </Button>
        {viaName(payer.name, forwardedTo) && (
          <small css={{ marginLeft: 6 }}>via {viaName(payer.name, forwardedTo)}</small>
        )}
        <div>
          <Button variant="link" css={linkStyle} onClick={switchToSearch}>
            Not this payer? Choose another
          </Button>
        </div>
      </>
    )
  } else if (candidates.length) {
    body = (
      <>
        <div>{`Several payers on file answer to that name — pick the ${rank}:`}</div>
        {picker(
          candidates.map((candidate) => ({
            value: candidate.id,
            label: payerLabel(candidate),
            matchedName: viaName(candidate.name, forwardedTo),
          })),
          'Choose a payer',
          false,
        )}
        <Button variant="link" css={linkStyle} onClick={switchToSearch}>
          None of these? Search all payers
        </Button>
      </>
    )
  } else {
    body = searchBody(`No matching payer on file — search for the ${rank} to add, or leave it for later.`)
  }

  return (
    <div css={{ fontSize: 11, color: '#6c757d', marginTop: 4 }}>
      <div>
        {`Forwarded to ${forwardedTo || 'another payer'}`}
        {claim.crossoverPayerId ? ` (payer id ${claim.crossoverPayerId})` : ''}
      </div>
      {body}
    </div>
  )
}

const ExceptionRow = ({ claim }: { claim: EraExceptionQueueItem }) => {
  const [showModal, setShowModal] = React.useState(false)
  const [planWanted, setPlanWanted] = React.useState(false)
  const canPostInsurancePayments = useLabPermission(UserPermission.POST_INSURANCE_PAYMENTS)
  const [archiveEraClaim] = useArchiveBillingEraClaimMutation()
  const reviewState = (claim.reviewState ?? 'POSTABLE') as EraClaimReviewState
  const stateColor = getClaimStateColor(reviewState)

  // Fetched when the biller reaches for Apply Payment. The modal reads this
  // same cache entry, so warming it here is what keeps the form from opening
  // on an empty table.
  useGetEraPostingPlanQuery(
    { eraId: claim.eraId as string, claimId: claim.id },
    { skip: !planWanted || !claim.eraId || !claim.id },
  )

  return (
    <>
      <StyledRow css={{ borderBottom: '1px solid #ededed' }}>
        <td css={{ whiteSpace: 'nowrap' }}>
          <span css={{ color: stateColor, fontWeight: 600, fontSize: 12 }}>
            {getClaimStateLabel(reviewState)}
          </span>
          <StateReason claim={claim} />
        </td>
        <td css={{ whiteSpace: 'nowrap' }}>
          <AutoPostCell claim={claim} />
        </td>
        <td css={{ fontWeight: 600 }}>
          <ClaimIdentifierLink claim={claim} />
        </td>
        <td css={{ fontWeight: 600 }}>{claim.payerClaimControlNumber || '-'}</td>
        <td>
          <ClaimPatientName claim={claim} />
          {claim.memberId && (
            <div css={{ fontSize: 11, color: '#6c757d' }}>{claim.memberId}</div>
          )}
        </td>
        <td>{formatCalendarDate(claim.dateOfService)}</td>
        <td>
          <Money v={claim.paidAmount} />
        </td>
        <td>
          {claim.responsible || claim.payerName || '-'}
          {claim.checkNumber && (
            <div css={{ fontSize: 11, color: '#6c757d' }}>Check {claim.checkNumber}</div>
          )}
        </td>
        <td>
          <ChargeCell claim={claim} />
          <CrossoverSuggestion claim={claim} />
        </td>
        <td css={{ whiteSpace: 'nowrap' }}>
          {canPostInsurancePayments && (
            <>
              <Button
                size="sm"
                variant="outline-primary"
                onMouseEnter={() => setPlanWanted(true)}
                onFocus={() => setPlanWanted(true)}
                onClick={() => {
                  setPlanWanted(true)
                  setShowModal(true)
                }}>
                {getPostingActionLabel(reviewState)}
              </Button>
              <Button
                size="sm"
                className="ml-2"
                variant="outline-secondary"
                onClick={() => {
                  confirm('', {
                    title: 'Are you sure?',
                    description:
                      'Archiving removes this claim from the exception queue. It can be un-archived from the ERA file.',
                    yesAction: 'Yes',
                  })
                    .then(async () => {
                      try {
                        await archiveEraClaim({
                          id: claim.eraId as string,
                          eraId: claim.eraId as string,
                          claimId: claim.id,
                          archived: true,
                        }).unwrap()
                        toast.success('Claim archived successfully')
                      } catch (e) {
                        toast.error('There was an error archiving this claim')
                      }
                    })
                    .catch(() => {})
                }}>
                Archive
              </Button>
            </>
          )}
        </td>
      </StyledRow>
      <ApplyPaymentModal
        open={showModal}
        onClose={() => setShowModal(false)}
        claim={claim}
        eraId={claim.eraId as string}
        claimId={claim.claimNumber as string}
      />
    </>
  )
}

export const EraExceptionQueue = () => {
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(25)
  const [search, setSearch] = React.useState('')
  const [reviewState, setReviewState] = React.useState<EraClaimReviewState | ''>('')
  const [matchStatus, setMatchStatus] = React.useState<EraClaimMatchStatus | ''>('')
  const [autoPostState, setAutoPostState] = React.useState<EraAutoPostState | ''>('')
  const [testType, setTestType] = React.useState<TestCode | ''>('')

  const [debouncedSearch] = useDebounce(search, 300)

  const query = React.useMemo<EraExceptionQueueQuery>(
    () => ({
      page,
      size: pageSize,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(reviewState ? { reviewState } : {}),
      ...(matchStatus ? { matchStatus } : {}),
      ...(autoPostState ? { autoPostState } : {}),
      ...(testType ? { testType } : {}),
    }),
    [page, pageSize, debouncedSearch, reviewState, matchStatus, autoPostState, testType],
  )

  const { data, isLoading, isFetching } = useGetEraExceptionQueueQuery(query)

  // Any filter change re-reads from the first page; staying on page 7 of a
  // freshly narrowed result set shows an empty table.
  React.useEffect(() => {
    setPage(0)
  }, [debouncedSearch, reviewState, matchStatus, autoPostState, testType])

  if (isLoading) {
    return <BillingLoading />
  }

  const items: EraExceptionQueueItem[] = data?.items ?? []

  return (
    <div>
      <div className="row mb-2">
        <div className="col-12 col-md-4">
          <label className="textGray">Search</label>
          <Input
            value={search}
            name="search"
            placeholder="Patient, policyholder, Claim Identifier, member ID or payer"
            handleChange={(e) => setSearch(e.target.value)}
            className="form-control"
            parentDiv={null}
          />
        </div>
        {/* Both dropdowns take their blank "everything" choice from Select's
            own default option. An option passed in with an empty key falls
            back to rendering its label as the value, so it would reach the
            API as though the label itself were a filter. */}
        <div className="col-6 col-md-3">
          <label className="textGray">State</label>
          <Select
            value={reviewState}
            name="reviewState"
            handleChange={(e) => setReviewState(e.target.value as EraClaimReviewState | '')}
            defaultLabel="Exceptions (default)"
            options={REVIEW_STATE_OPTIONS}
          />
        </div>
        <div className="col-6 col-md-3">
          <label className="textGray">Charge match</label>
          <Select
            value={matchStatus}
            name="matchStatus"
            handleChange={(e) => setMatchStatus(e.target.value as EraClaimMatchStatus | '')}
            defaultLabel="Any"
            options={MATCH_STATUS_OPTIONS}
          />
        </div>
        <div className="col-6 col-md-3">
          <label className="textGray">Auto-post</label>
          <Select
            value={autoPostState}
            name="autoPostState"
            handleChange={(e) => setAutoPostState(e.target.value as EraAutoPostState | '')}
            defaultLabel="Any"
            options={AUTO_POST_STATE_OPTIONS}
          />
        </div>
        <div className="col-6 col-md-3">
          <label className="textGray">Test type</label>
          <Select
            value={testType}
            name="testType"
            handleChange={(e) => setTestType(e.target.value as TestCode | '')}
            defaultLabel="Any"
            options={ERA_TEST_TYPE_OPTIONS}
          />
        </div>
        <div className="col-12 col-md-2 d-flex align-items-end">
          <span css={{ fontSize: 13, color: '#495057' }}>
            {data?.totalItems ?? 0} claim{(data?.totalItems ?? 0) === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      <div className="table-responsive" css={{ opacity: isFetching ? 0.6 : 1 }}>
        <table className="table table-sm mb-0">
          <thead>
            <tr>
              <th>State</th>
              <th>Auto-post</th>
              <th>Claim Identifier</th>
              <th>Claim Number</th>
              <th>Patient (Policyholder)</th>
              <th>DOS</th>
              <th>Paid</th>
              <th>Payer</th>
              <th>Charge</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={10} className="text-center p-4">
                  No records found
                </td>
              </tr>
            ) : (
              items.map((claim: EraExceptionQueueItem) => (
                <ExceptionRow key={claim.id} claim={claim} />
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        className="pagination-bar"
        currentPage={page}
        totalCount={data?.totalItems || 1}
        pageSize={pageSize}
        siblingCount={1}
        setPageSize={setPageSize}
        onPageChange={setPage}
      />
    </div>
  )
}
